<template>
    <Spinner v-if="displaySpinner" />
    <div class="title-and-controls">
        <div class="page-title">
            <span class="page-title">
                <i class="pi pi-window-minimize" />
                <h1>{{ flowName }}</h1>
            </span>
            <Tag
                icon="pi pi-exclamation-triangle"
                severity="warn"
                value="Archived"
                v-if="isArchived"
            ></Tag>
        </div>
        <div class="controls">
            <template v-if="isArchived">
                <Button
                    class="p-button-secondary"
                    severity="danger"
                    icon="pi pi-trash"
                    label="Delete"
                    @click="handleDeleteArchive"
                />
            </template>
            <template v-else>
                <Button
                    v-if="description && !description.content && isEditing"
                    v-tooltip.bottom="{
                        value: 'Will generate a first draft of the description based on the context.',
                        showDelay: 100,
                    }"
                    severity="secondary"
                    icon="pi pi-sparkles"
                    :label="
                        description?.content
                            ? 'Regenerate'
                            : 'Generate first draft'
                    "
                    @click="generateFlowDescription"
                />
            </template>
        </div>
    </div>
    <section class="description" :class="{ 'edit': isEditing }">
        <TextAppArtefact
            v-if="flow?.identity && description"
            v-model="description"
            :entityId="flow.identity"
            :entityType="EntityType.FLOW"
            :isEditing="isEditing"
        />
    </section>
</template>

<script setup lang="ts">
import { baseSuccessToastMessage } from "@/primevue/init";
import TextAppArtefact from "@/components/TextAppArtefact.vue";
import Spinner from "@/shared/Spinner.vue";
import { computed, ref, watch } from "vue";
import { useToast } from "primevue/usetoast";
import { useConfirm } from "primevue/useconfirm";
import { useFlowManager, useFlowStore } from "@/stores/flow-store";
import { useEditManager } from "@/composables/edit-manager";
import { EntityType } from "@/stores/entity-store";
import { useGenerateDescriptionDialog } from "@/composables/generate-description";

const toast = useToast();
const { deleteFlow } = useFlowStore();
const { isEditing } = useEditManager();
const { isFetching, description, flow } = useFlowManager();
const { generateContent } = useGenerateDescriptionDialog();

const confirm = useConfirm();

const displaySpinner = ref(false);

const isArchived = computed(() => {
    return flow.value?.is_archived;
});

const handleDeleteArchive = async (event: Event) => {
    if (!isArchived.value) {
        toast.add({
            severity: "error",
            summary: "Unable to delete flow. Flow is not archived.",
        });
        return;
    }

    confirm.require({
        target: event.currentTarget as HTMLElement,
        message: "Are you sure you want to permanently delete this flow?",
        icon: "pi pi-exclamation-triangle",
        rejectProps: {
            label: "Cancel",
            severity: "secondary",
            outlined: true,
        },
        acceptProps: {
            label: "Delete",
            severity: "danger",
        },
        accept: async () => {
            try {
                displaySpinner.value = true;
                await deleteFlow();
                toast.add({
                    ...baseSuccessToastMessage,
                    summary: "Flow has been permanently deleted",
                });
            } catch {
                toast.add({
                    severity: "error",
                    summary: "Could not delete flow",
                });
            } finally {
                displaySpinner.value = false;
            }
        },
    });
};

const flowName = computed(() => {
    if (!flow.value) {
        return "...";
    }

    return flow.value.name || flow.value.bpmn_id;
});

const generateFlowDescription = async () => {
    if (!flow.value) {
        return;
    }
    await generateContent(
        flow.value.identity,
        EntityType.FLOW,
        description.value?.updateContent.bind(description.value)
    );
};

watch(isFetching, (newValue) => (displaySpinner.value = newValue), {
    immediate: true,
});
</script>

<style scoped lang="scss">
.page-title {
    margin-left: 1rem;
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 0;

    i {
        font-size: 1.6rem;
    }
    .p-tag {
        margin-left: 0.5rem;
    }
}

section {
    border: 1px solid transparent;
    position: relative;
    padding: 1rem;

    &.description {
        padding-top: 0;
        z-index: 3;
    }

    &.edit {
        border-color: var(--stekz-outline);
        border-radius: 0.5rem;

        &.description {
            padding: 0;
        }
    }
}
</style>
